import type { AxiosInstance } from 'axios';
import queryString from 'query-string';
import type * as ChatApi from './interfaces/chat-instances.js';
import {
  transformChatInstance,
  transformChatSession,
  transformChatShareableUsers,
} from './transformers/index.js';

export class ChatInstanceClient {
  constructor(private authenticatedClient: AxiosInstance) {}

  getChatInstance = async (data: ChatApi.GetChatInstance.Request) => {
    const res = await this.authenticatedClient.get<ChatApi.GetChatInstance.Response>(
      `/chat/${data.chatInstanceIdentifier}`
    );
    return transformChatSession(res.data);
  };

  renameChatInstance = async ({ chatInstanceId, ...data }: ChatApi.RenameChatInstance.Request) => {
    const res = await this.authenticatedClient.patch<ChatApi.RenameChatInstance.Response>(
      `/chat/${chatInstanceId}/name`,
      data
    );
    return {
      chatInstance: transformChatInstance(res.data.chatInstance),
    };
  };

  deleteChatInstance = async (data: ChatApi.DeleteChatInstance.Request) => {
    const res = await this.authenticatedClient.delete(`/chat/${data.chatInstanceId}`);
    return res.data;
  };

  getChatShareableUsers = async (data: ChatApi.GetChatShareableUsers.Request) => {
    const qs = queryString.stringify(
      {
        search: data.search,
      },
      {
        skipNull: true,
      }
    );
    const res = await this.authenticatedClient.get<ChatApi.GetChatShareableUsers.Response>(
      `/chat/${data.chatInstanceId}/shareable-users?${qs}`
    );
    return {
      items: transformChatShareableUsers(res.data),
    };
  };

  shareChatInstance = async (data: ChatApi.ShareChatInstance.Request) => {
    return this.authenticatedClient.post(`/chat/${data.chatInstanceId}/share`, {
      standaloneLink: data.standaloneLink,
      userIdentifiers: data.userIdentifiers,
    });
  };

  downloadChatInstance = async (data: ChatApi.DownloadChatInstance.Request) => {
    return this.authenticatedClient.get<Blob>(`/chat/${data.chatInstanceId}/report`, {
      responseType: 'blob',
    });
  };

  toggleChatInstanceBookmark = async ({
    chatInstanceId,
    ...data
  }: ChatApi.ToggleChatInstanceBookmark.Request) => {
    const res = await this.authenticatedClient.patch<ChatApi.ToggleChatInstanceBookmark.Response>(
      `/chat/${chatInstanceId}/bookmark`,
      data
    );
    return {
      bookmarked: res.data.bookmarked,
    };
  };

  downloadIconnectArtifact = async (
    data: ChatApi.DownloadIconnectArtifact.Request
  ): Promise<{ blob: Blob; filename: string }> => {
    const res = await this.authenticatedClient.post<Blob>('/chat/iconnect-artifact', data, {
      responseType: 'blob',
    });
    const contentDisposition = res.headers['content-disposition'] as string | undefined;
    const filename = contentDisposition
      ? (/filename="([^"]+)"/.exec(contentDisposition)?.[1] ??
        `form-submission-${new Date().toISOString().split('T')[0]}.pdf`)
      : `form-submission-${new Date().toISOString().split('T')[0]}.pdf`;
    return { blob: res.data, filename };
  };
}
